import { forwardRef } from 'react';
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { cx, hasWidthClass } from './cx.ts';

/**
 * Text input / textarea with the Field Notes field chrome: a raised
 * white box on the paper card with a hairline border that darkens on
 * hover and switches to forest on focus.
 *
 * Width defaults to `w-full` (forms), but a `w-*` in `className` wins —
 * see `hasWidthClass`; a hardcoded base `w-full` would otherwise
 * override a caller's fixed width via Tailwind source order.
 */
const FIELD_CLASSES =
  'rounded border border-line bg-raised px-3 text-body text-ink placeholder:text-ink-faint transition-colors duration-150 hover:border-line-strong focus:border-forest focus:outline-none focus-visible:ring-2 focus-visible:ring-forest-bright/30 disabled:pointer-events-none disabled:opacity-60';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  readonly mono?: boolean;
  readonly invalid?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { mono = false, invalid = false, className, ...rest },
  ref,
): JSX.Element {
  return (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={cx(
        FIELD_CLASSES,
        'h-9',
        !hasWidthClass(className) && 'w-full',
        mono && 'font-mono',
        invalid && 'border-bad-line',
        className,
      )}
      {...rest}
    />
  );
});

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  readonly mono?: boolean;
  readonly invalid?: boolean;
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { mono = false, invalid = false, className, rows, ...rest },
  ref,
): JSX.Element {
  return (
    <textarea
      ref={ref}
      rows={rows ?? 4}
      aria-invalid={invalid || undefined}
      className={cx(
        FIELD_CLASSES,
        'py-2',
        !hasWidthClass(className) && 'w-full',
        mono && 'font-mono',
        invalid && 'border-bad-line',
        className,
      )}
      {...rest}
    />
  );
});
